import CollectionsOutlinedIcon from "@mui/icons-material/CollectionsOutlined";
import DesktopWindowsOutlinedIcon from "@mui/icons-material/DesktopWindowsOutlined";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import ListAltOutlinedIcon from "@mui/icons-material/ListAltOutlined";
import { Box, Button, colors, Container, Divider, styled } from "@mui/material";
import TouchRipple from "@mui/material/ButtonBase";
import { FlexBox } from "components/flex-box";
import { H6 } from "components/Typography";
import Link from "next/link";
import { layoutConstant } from "utils/constants";
import { navigationRoutes } from "common/constants/navigation-routes";
import LazyImage from "components/LazyImage";
import { blogsFlag, promotionalsFlag } from "common/constants/feature-toggles";
import modules from "modules";
import { useAppSelector } from "hooks/use-app-selector";
import { usePromotionalCounts } from "hooks/promotional/usePromotionalCounts";
import { useRouter } from "next/router";
import { isMobile } from "react-device-detect";
import useResolution from "hooks/useResolution";

const { selectors: promotionalSelectors } = modules.promotional;

const TopbarWrapper = styled(Box)(({ theme }) => ({
  fontSize: 12,
  height: layoutConstant.topbarHeight,
  background: theme.palette.background.paper,
  color: theme.palette.common.black,
  "& .topbarLeft": {
    "& .logo": { display: "none" },
    "& .title": { marginLeft: "10px" },
    "@media only screen and (max-width: 900px)": {
      "& .logo": { display: "block" },
      "& > *:not(.logo)": { display: "none" },
    },
  },
  "& .topbarRight": {
    "& .link": {
      paddingRight: 30,
      color: theme.palette.secondary.contrastText,
    },

    "@media only screen and (max-width: 900px)": {
      "& .link": { display: "none" },
    },
  },
  "& .menuItem": { minWidth: 100 },
  "& .marginRight": { marginRight: "1.25rem" },
  "& .handler": { height: layoutConstant.topbarHeight },
  "& .smallRoundedImage": { height: 15, width: 25, borderRadius: 2 },
  "& .menuTitle": { fontSize: 12, marginLeft: "0.5rem", fontWeight: 600 },
}));

const Topbar = () => {
  const { publishedCounts } = usePromotionalCounts();
  const router = useRouter();
  const { isSm, isMd, isLg } = useResolution();

  return (
    <TopbarWrapper
      style={{
        marginBottom: isSm ? 45 : isMd ? 25 : isLg ? 5 : 5,
      }}
    >
      <Divider />
      <div>
        <div
          style={{
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "10px",
          }}
        >
          <FlexBox className="topbarRight" alignItems="center">
            <Link href={navigationRoutes.aiBuilder} passHref>
              <TouchRipple sx={{ marginRight: 3 }}>
                <SmartToyOutlinedIcon
                  color="secondary"
                  sx={{ marginRight: 0.5 }}
                />
                <H6 sx={{ color: "#373F50" }}>AI Builder</H6>
              </TouchRipple>
            </Link>
            <Link href={navigationRoutes.diyBuilder} passHref>
              <TouchRipple sx={{ marginRight: 3 }}>
                <DesktopWindowsOutlinedIcon
                  color="secondary"
                  sx={{ marginRight: 0.5 }}
                />
                <H6 sx={{ color: "#373F50" }}>PC Builder</H6>
              </TouchRipple>
            </Link>
            <Link href={navigationRoutes.buildGallery} passHref>
              <TouchRipple>
                <CollectionsOutlinedIcon
                  color="secondary"
                  sx={{ marginRight: 0.5 }}
                />
                <H6 sx={{ color: "#373F50" }}>PC Gallery</H6>
              </TouchRipple>
            </Link>
            <Link href={navigationRoutes.pricelist} passHref>
              <TouchRipple sx={{ marginLeft: 3 }}>
                <ListAltOutlinedIcon
                  color="secondary"
                  sx={{ marginRight: 0.5 }}
                />
                <H6 sx={{ color: "#373F50" }}>Pricelist</H6>
              </TouchRipple>
            </Link>
            <Link href={navigationRoutes.blogs} passHref>
              <TouchRipple sx={{ ml: 3 }}>
                <LazyImage
                  height="24px"
                  width="24px"
                  src={"/assets/images/badges/Frame.png"}
                />
                <H6 sx={{ marginLeft: 1, color: "#373F50" }}>
                  Blogs & Articles
                </H6>
              </TouchRipple>
            </Link>
            <Link href={navigationRoutes.powered_builds} passHref>
              <TouchRipple sx={{ marginLeft: 3 }}>
                <LazyImage
                  height="20px"
                  width="24px"
                  src={"/assets/images/badges/powered_builds.png"}
                />
                <H6 sx={{ color: "#373F50", ml: 1 }}>Powered Builds</H6>
              </TouchRipple>
            </Link>

            {promotionalsFlag && (
              <Link href={navigationRoutes.promotionals} passHref>
                <TouchRipple
                  sx={{
                    ml: 3,
                    pb: "1px",
                    height: "2em",
                    overflow: "visible",
                    display: "flex",
                    alignItems: "end",
                  }}
                >
                  <div
                    style={{
                      overflow: "visible",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <FlexBox sx={{ justifyContent: "end", mb: "-.4em" }}>
                      {!!publishedCounts && (
                        <LazyImage
                          height="14px"
                          width="51px"
                          objectFit="contain"
                          src="/assets/images/badges/hot-deals.png"
                        />
                      )}
                    </FlexBox>
                    <FlexBox>
                      <LazyImage
                        height="20px"
                        width="24px"
                        src={"/assets/images/badges/promo.png"}
                      />
                      <H6 sx={{ color: "#373F50", ml: 1 }}>Promos</H6>
                    </FlexBox>
                  </div>
                </TouchRipple>
              </Link>
            )}
            <Link href={navigationRoutes.extra_service} passHref>
              <TouchRipple sx={{ marginLeft: 3 }}>
                <FlexBox columnGap={1}>
                  <LazyImage
                    src="/assets/images/extra-service/pc-worth-extra-service-seal.png"
                    layout="intrinsic"
                    height={21.27}
                    width={21.33}
                  />
                  <H6 sx={{ color: "#CF151A" }}>PC Worth Extra Service</H6>
                </FlexBox>
              </TouchRipple>
            </Link>
          </FlexBox>
        </div>

        <Divider />

        <Box
          sx={{
            height: "55px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "#ffffff",
            width: "100%",
            paddingBottom: "0.5em",

            marginBottom: "auto",
          }}
        >
          {/* remove later */}
          {/* <H6 sx={{ marginLeft: 3, color: "#FFFFFF", ml: 1 }}>Powered By</H6> */}

          <FlexBox
            className="topbarRight"
            alignItems="center"
            justifyContent="end"
          >
            <Button
              component="a"
              href={navigationRoutes.poweredBy.replace(":id", "gigabyte")}
              sx={{
                ml: 1,
                height: "inherit",
                pb: ".1em",
                mt: 0.5,
                textTransform: "none",
                padding: "6px 60px",
                border: "1px solid #aeb4be",
                borderRadius: "4px",
                backgroundColor:
                  router.query.id === "gigabyte" ? colors.grey[300] : null,
              }}
            >
              <LazyImage
                width={98}
                height={33}
                layout="intrinsic"
                src={"/assets/images/brands/gigabyte2025.png"}
              />
            </Button>

            <Button
              component="a"
              href={navigationRoutes.poweredBy.replace(":id", "asus")}
              sx={{
                ml: 1,
                height: "inherit",
                pb: ".1em",
                mt: 0.5,
                textTransform: "none",
                padding: "7px 44px",
                border: "1px solid #aeb4be",
                borderRadius: "4px",
                backgroundColor:
                  router.query.id === "asus" ? colors.grey[300] : null,
              }}
            >
              <LazyImage
                width={135}
                height={29.03}
                layout="intrinsic"
                src={"/assets/images/brands/asus2025.png"}
              />
            </Button>

            <Button
              component="a"
              href={navigationRoutes.poweredBy.replace(":id", "asrock")}
              sx={{
                ml: 1,
                height: "inherit",
                pb: ".1em",
                mt: 0.5,
                textTransform: "none",
                padding: "5px 20px",
                border: "1px solid #aeb4be",
                borderRadius: "4px",
                backgroundColor:
                  router.query.id === "asrock" ? colors.grey[300] : null,
              }}
            >
              <LazyImage
                width={183.39}
                height={33.09}
                layout="intrinsic"
                src={"/assets/images/brands/asrock2025.png"}
              />
            </Button>

            <Button
              component="a"
              href={navigationRoutes.poweredBy.replace(":id", "msi")}
              sx={{
                ml: 1,
                height: "inherit",
                pb: ".1em",
                mt: 0.5,
                textTransform: "none",
                padding: "5px 60px",
                border: "1px solid #aeb4be",
                borderRadius: "4px",
                backgroundColor:
                  router.query.id === "msi" ? colors.grey[300] : null,
              }}
            >
              <LazyImage
                width={92.38}
                height={33.07}
                layout="intrinsic"
                src={"/assets/images/brands/msi2025.png"}
              />
            </Button>

            <Button
              component="a"
              href={navigationRoutes.amdGamersFirst}
              sx={{
                height: "inherit",
                pb: ".1em",
                ml: 1,
                mt: 0.5,
                textTransform: "none",
                padding: "7px 50px",
                border: "1px solid #aeb4be",
                borderRadius: "4px",
                backgroundColor:
                  router.query.id === "gigabyte" ? colors.grey[300] : null,
              }}
            >
              <LazyImage
                width={66}
                height={29}
                layout="intrinsic"
                src={"/assets/images/brands/amd-header-logo.png"}
              />
            </Button>

            <Button
              component="a"
              href={navigationRoutes.nvidia}
              sx={{
                height: "inherit",
                pb: ".1em",
                ml: 1,
                mt: 0.5,
                textTransform: "none",
                padding: "6px 40px",
                border: "1px solid #aeb4be",
                borderRadius: "4px",
              }}
            >
              <LazyImage
                width={105}
                height={32}
                layout="intrinsic"
                src={"/assets/images/brands/nvidia.png"}
                sx={{
                  maxHeight: 32,
                  objectFit: "cover",
                }}
              />
            </Button>

            <Button
              component="a"
              href={navigationRoutes.brands}
              sx={{
                height: "inherit",
                pb: ".1em",
                ml: 1,
                mt: 0.5,
                textTransform: "none",
                padding: "6px 40px",
                border: "1px solid #aeb4be",
                borderRadius: "4px",
              }}
            >
              <LazyImage
                width={105}
                height={32}
                layout="intrinsic"
                src={"/assets/images/brands/brands-header-logo.png"}
              />
            </Button>
          </FlexBox>
        </Box>
      </div>
    </TopbarWrapper>
  );
};

export default Topbar;
